import React, { useCallback, useContext, useState, useEffect } from 'react';
import { Form } from 'src/core/forms';
import { FormInitialValues } from 'src/components/CustomisationPage/pages/Configure';
import { ProductSwatch } from 'src/components/ProductDetail/ProductSwatch';
import { BundleProduct } from 'src/components/ProductDetail/utils/product-detail.types';
import { ProductDetailLayout } from 'src/components/ProductDetail/ProductDetailLayout';
import { RichText } from 'src/components/RichText/RichText';
import {
    BundleOptionsContext,
    BundleOptionsProvider,
    ArtworkPriceBreakProps,
} from 'src/components/BundleProduct/BundleOptionsProvider';
import { ErrorOrOfflineConnected } from 'src/components/Error/ErrorOrOffline';
import { useConstant } from 'src/core/hooks/react-hooks';
import { Subject } from 'rxjs';
import { Size } from 'src/components/ProductDetail/ProductSizes';
import { Swatch } from 'src/components/ProductDetail/ProductSwatch';
import { BundleAtbModal } from 'src/components/BundleProduct/BundleAtbModal';
import { ProductSchema } from 'src/components/ProductDetail/ProductStructuredData';
import { QuoteDataCartItemInterface } from 'swagger/ts/Definitions';
import { BundleMsg, bundleRegister } from 'src/components/BundleProduct/feature/bundle.register';
import { useDispatch } from 'react-redux';
import { useLazyRegister } from 'src/core/engine/hooks/useRegisterFeature';
import { ProductLocation } from 'src/components/CustomisationPage/feature/customisation.types';
import { Button } from 'src/components/Button/Button';
import { useFormDataCausesRender } from 'src/components/BundleProduct/BundleSelectionProvider';
import { bundleCustomisationProps } from 'src/components/BundleProduct/utils/formToCustomisation';
import AnimateHeight from 'react-animate-height';
import { validateArtwork } from 'src/util/validateArtwork';
import { BundleProductContent } from 'src/components/BundleProduct/BundleProductContent';
import { ApolloError } from '@apollo/client';
import { Container, Wrapper } from 'src/components/Layout';
import { ApplicationMethod } from 'src/components/CustomisationPage/feature/customisation.types';
import { gtmAddToCartWithVersion } from 'src/features/analytics/epics/gtmAddToCartWithVersion';
import { AddBundleVersion } from 'src/features/analytics/utils/eventTypes';
import { useBundleOptions } from 'src/hooks/useBundleOptions';
import { convertFormDataToPayload } from 'src/components/BundleProduct/utils/convertFormDataToPayload';
import { FormData } from 'src/components/BundleProduct/types/index';
import { BundlePageSkeleton } from 'src/elements/Skeletons/Skeletons';
import { BUNDLE_MAX_CUSTOMISATIONS } from 'src/constants/customisation';
import { useBundleOptionsPrice } from 'src/hooks/useBundleOptionsPrice';

import { Skeleton } from '../Common/Skeleton';

import { BundleSelectionProvider } from './BundleSelectionProvider';
import { BundleCustomisation } from './BundleCustomisation';
import { BundleSizeList } from './BundleSizeList';
import { ProductBundleSummary } from './ProductBundleSummary/ProductBundleSummary';
import { BundleSummaryCount } from './ProductBundleSummary/ProductBundleSummaryElements';
import { RenderArtworks, organizeArtworkPriceInfo } from './ProductBundleSummary/helpers';
import classes from './ProductBundle.scss';
import { useIsSizesDisabled } from './BundleSelectionProvider';
import { BundleSectionTitle } from './BundleSectionTitle';

type ProductBundleProps = {
    product: BundleProduct;
    initialValues: FormInitialValues;
    reMountForm();
    setBundleColor?: React.Dispatch<React.SetStateAction<string[]>>;
    customisationOption: string;
};

export const BundleProductPage: React.FC<ProductBundleProps> = (props) => {
    useLazyRegister(bundleRegister, true, 'BundleSelectionProvider');
    const dispatch = useDispatch();
    const { product } = props;
    const bundlePrice = useBundleOptionsPrice(product.id);

    const submit$ = useConstant(() => new Subject());

    const onSubmit = useCallback(
        (values) => {
            submit$.next(values);
        },
        [submit$],
    );

    const onComplete = useCallback(
        (payload: QuoteDataCartItemInterface) => {
            gtmAddToCartWithVersion(AddBundleVersion.V2, product, undefined, { price: bundlePrice });
            dispatch(BundleMsg('Bundle.Add', payload));
        },
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [dispatch, bundlePrice],
    );

    return (
        <Wrapper variant="page-inner">
            <Container>
                <Form className={classes.form} onSubmit={onSubmit} data-test-id="BundleProductPage_Form">
                    <BundleOptionsProvider id={product.id}>
                        <BundleSelectionProvider
                            sku={product.sku}
                            id={product.id}
                            submit$={submit$}
                            onSubmit={onComplete}
                        >
                            <BundleDetail {...props} />
                        </BundleSelectionProvider>
                    </BundleOptionsProvider>
                </Form>
            </Container>
        </Wrapper>
    );
};

export const ModalBundleProductPage: React.FC<ProductBundleProps> = ({ product, customisationOption }) => {
    useLazyRegister(bundleRegister, true, 'BundleSelectionProvider');
    const dispatch = useDispatch();

    const bundlePrice = useBundleOptionsPrice(product.id);
    const bundleData = useBundleOptions(product.id);

    if (!bundleData?.artworkPriceBreak) {
        return (
            <Wrapper variant="page-inner" bg="white">
                <BundlePageSkeleton />
            </Wrapper>
        );
    }

    const onSubmit = (data: FormData) => {
        dispatch(BundleMsg('Bundle.Add', convertFormDataToPayload(data, product.sku, bundleData.bundles)));
    };

    return (
        <Wrapper variant="page-inner" bg="white">
            <BundleProductContent
                product={product}
                bundles={bundleData.bundles}
                bundlePrice={bundlePrice}
                artworkPriceBreak={bundleData.artworkPriceBreak}
                maxCustomisations={BUNDLE_MAX_CUSTOMISATIONS}
                onSubmit={onSubmit}
                customisationOption={customisationOption}
            />
        </Wrapper>
    );
};

type BundleOptionsProps = {
    product: BundleProduct;
    initialValues: FormInitialValues;
    reMountForm();
    setBundleColor?: React.Dispatch<React.SetStateAction<string[]>>;
    loading: boolean;
    error: ApolloError | undefined;
    swatches: Swatch[];
    sizes: Size[];
    artworksData: bundleCustomisationProps;
    artworkPriceBreak: ArtworkPriceBreakProps;
    maxQty: number;
};

const BundleOptions: React.FC<BundleOptionsProps> = (props) => {
    const {
        product,
        reMountForm,
        setBundleColor,
        loading,
        swatches,
        error,
        sizes,
        artworksData,
        artworkPriceBreak,
        maxQty,
    } = props;

    if (error) return <ErrorOrOfflineConnected message={error.message} />;

    return (
        <>
            <div className={classes.productDetailShort}>
                <RichText content={product.short_description || 'missing short_description'} />
            </div>
            <h2 className={classes.bundleHeader}>Create your bundle!</h2>
            <div className={classes.bundleSummaryCount}>
                <b>Bundle Summary</b> <BundleSummaryCount />
            </div>
            {loading && <Skeleton />}
            {!loading && (
                <div>
                    <ProductSwatch
                        title="1. Select colour(s)"
                        swatches={swatches}
                        count={swatches.length}
                        isBundleView
                        setBundleColor={setBundleColor}
                        sizes={sizes}
                    />
                    <BundleSizeList sizes={sizes} swatches={swatches} sizeGuide={product.size_guides} />
                    <BundleCusotmisations
                        initialValues={props.initialValues}
                        locations={props.product.locations}
                        recommendedApplication={props.product.recommended_application}
                        artworksData={artworksData}
                        artworkPriceBreak={artworkPriceBreak}
                        maxQty={maxQty}
                    />
                    <BundleAtbModal product={product} reMountForm={reMountForm} />
                </div>
            )}
        </>
    );
};

const BundleDetail: React.FC<ProductBundleProps> = React.memo((props) => {
    const { product } = props;
    const { bundlePrice, artworkPriceBreak, maxQty, loading, swatches, error, sizes } =
        useContext(BundleOptionsContext);

    const [bundleColor, setBundleColor] = useState<string[]>([]);
    const [sumBundlePrice, setSumBundlePrice] = useState<{ price: number; price_incl: number }>({
        price: 0,
        price_incl: 0,
    });
    const setSumBundlePriceCallback = React.useCallback((data) => setSumBundlePrice(data), [setSumBundlePrice]);
    const artworksData = useFormDataCausesRender();

    return (
        <ProductDetailLayout
            product={product}
            price={bundlePrice}
            media_entries={product.media_entries}
            showSku={false}
            showPrice={true}
            isBundle={true}
            beforeReviews={
                <div className={classes.productDetailBundleInformation}>
                    <ProductBundleSummary
                        setSumBundlePrice={setSumBundlePriceCallback}
                        artworkPriceBreak={artworkPriceBreak as ArtworkPriceBreakProps}
                        maxQty={maxQty}
                        bundlePrice={bundlePrice}
                        artworksData={artworksData.artworksData}
                    />
                </div>
            }
            bundleColor={bundleColor}
            sumBundlePrice={sumBundlePrice}
            isRegularView={true}
        >
            <ProductSchema product={product} productPrice={bundlePrice} />
            <BundleOptions
                {...props}
                setBundleColor={setBundleColor}
                loading={loading}
                swatches={swatches}
                error={error}
                sizes={sizes}
                artworksData={artworksData.artworksData}
                artworkPriceBreak={artworkPriceBreak as ArtworkPriceBreakProps}
                maxQty={maxQty}
            />
        </ProductDetailLayout>
    );
});

type BundleCusotmisationsProps = {
    initialValues: FormInitialValues;
    locations: ProductLocation[];
    sizesAreHidden?: boolean;
    artworksData: bundleCustomisationProps;
    artworkPriceBreak: ArtworkPriceBreakProps;
    maxQty: number;
    recommendedApplication?: ApplicationMethod;
};

const BundleCusotmisations: React.FC<BundleCusotmisationsProps> = ({
    initialValues,
    locations,
    sizesAreHidden,
    artworksData,
    artworkPriceBreak,
    maxQty,
    recommendedApplication,
}) => {
    const isDisabled = useIsSizesDisabled(sizesAreHidden);
    const [artworks, setArtworks] = useState<number[]>([0]);
    const [open, setOpen] = useState<number>(0);
    const { isValid } = artworksData && validateArtwork(artworksData);
    const artworkId = artworksData[1] && artworksData[1].artwork;

    // After opening new artwork, scroll to its position
    useEffect(() => {
        const element = document.getElementById(`artwork-anchor-id-0`);
        element && element.scrollIntoView({ behavior: 'smooth' });
    }, [open]);

    const freeLogoTitle = '3. Add FREE Logo';
    const additionalLogoTitle = '4. Add Additional Logo';
    if (isDisabled) {
        return (
            <>
                <BundleSectionTitle title={freeLogoTitle} disabled />
                <BundleSectionTitle title={additionalLogoTitle} disabled />
            </>
        );
    }

    const newArtwork = () => {
        setOpen(artworks.length);
        setArtworks([...artworks, artworks.length]);
    };

    const deleteArtwork = (index: number) => {
        const filterArtworks = artworks.filter((x) => x !== index);
        const newIndexList = Array.from(Array(filterArtworks.length).keys());
        setArtworks(newIndexList);
    };

    return (
        <div className={classes.bundleCustomisations}>
            <div>
                {artworks.map((artwork, index) => {
                    const active = open === index;
                    return (
                        <div key={index}>
                            <div className={classes.titleContainer}>
                                <BundleSectionTitle
                                    title={index === 0 ? freeLogoTitle : additionalLogoTitle}
                                    disabled={false}
                                    id={index}
                                    anchor={index === 0}
                                />
                                {index >= 1 && (
                                    <Button width="full" variant="light" onClick={() => deleteArtwork(index)}>
                                        Delete artwork
                                    </Button>
                                )}
                            </div>

                            <AnimateHeight id={`panel-closed-${index}`} duration={200} height={active ? 0 : 'auto'}>
                                <ClosedBundleCustomisation
                                    setOpen={setOpen}
                                    index={index}
                                    artworksData={artworksData}
                                    artworkPriceBreak={artworkPriceBreak}
                                    maxQty={maxQty}
                                />
                            </AnimateHeight>

                            <AnimateHeight id={`panel-${index}`} duration={200} height={active ? 'auto' : 0}>
                                <BundleCustomisation
                                    initialValues={initialValues}
                                    locations={locations}
                                    recommendedApplication={recommendedApplication}
                                    fieldIndex={index + 1}
                                    numberOfArtworks={Object.keys(artworksData).length}
                                />
                            </AnimateHeight>
                        </div>
                    );
                })}
            </div>

            {artworks.length === 2 || !isValid || artworkId === 'none' ? null : (
                <div className={classes.additionalBundleCustomisation}>
                    <h2 className={classes.header}>{additionalLogoTitle}</h2>
                    <Button type="button" onClick={() => newArtwork()} variant="bold">
                        Add another logo to this item
                    </Button>
                </div>
            )}
        </div>
    );
};

type ClosedBundleCustomisationProps = {
    index: number;
    artworksData: bundleCustomisationProps;
    setOpen: React.Dispatch<React.SetStateAction<number>>;
    artworkPriceBreak: ArtworkPriceBreakProps;
    maxQty: number;
};

const ClosedBundleCustomisation: React.FC<ClosedBundleCustomisationProps> = (props) => {
    const { setOpen, index, artworksData, artworkPriceBreak, maxQty } = props;

    const { artworkCost } = organizeArtworkPriceInfo({ artworkPriceBreak, artworksData, maxQty });

    return <RenderArtworks setOpen={setOpen} index={index} artworksData={artworksData} artworkCost={artworkCost} />;
};
